import { useCallback, useEffect, useState } from "react";
import { secondAxios } from "../api/axios";
import { useAuthContext } from "../context/authContext";
import { isSameClientRef } from "../utils/clientPrefix";

/**
 * Fetches the plants/projects visible to the current user's client —
 * same visibility rule used by the main Projects table (admin client sees
 * everything, everyone else is filtered to their own client).
 */
export default function useClientPlants() {
  const { user } = useAuthContext();
  const [plants, setPlants] = useState([]);
  const [loading, setLoading] = useState(true);
  // Bumped by reloadPlants() to re-run the fetch - e.g. after a new plant is
  // created from the projects tree.
  const [reloadToken, setReloadToken] = useState(0);

  const reloadPlants = useCallback(() => setReloadToken((n) => n + 1), []);

  // Applies an already-saved edit to the cached list. reloadPlants() puts
  // the whole list back into its loading state, which blanks the tree for
  // the length of a fetch; this shows the change at once, and the refetch
  // that follows just confirms it.
  const patchPlant = useCallback((plantId, changes) => {
    if (!plantId || !changes) return;

    setPlants((prev) =>
      prev.map((plant) =>
        String(plant?.id) === String(plantId)
          ? { ...plant, ...changes }
          : plant,
      ),
    );
  }, []);

  useEffect(() => {
    if (!user?.client) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchPlants = async () => {
      try {
        setLoading(true);
        const res = await secondAxios.get("/plant");
        const adminClient = process.env.REACT_APP_CLIENT_NAME;
        const data = Array.isArray(res.data) ? res.data : [];

        const filtered = isSameClientRef(user.client, adminClient)
          ? data
          : data.filter(
              (plant) =>
                isSameClientRef(plant.client?.name, user.client) ||
                isSameClientRef(plant.client?.prefix, user.client),
            );

        if (!cancelled) setPlants(filtered);
      } catch (err) {
        console.error("Error fetching plants:", err);
        if (!cancelled) setPlants([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchPlants();

    return () => {
      cancelled = true;
    };
  }, [user?.client, reloadToken]);

  return { plants, loading, reloadPlants, patchPlant };
}
